/**
 * `resolve` — the engine's central function, and what makes lazy generation safe.
 *
 *   resolve(primitiveId, version, variant, themeId) → VariantInstance
 *     • compute the content-addressed id
 *     • an instance exists and is ready  → reuse it (FREE)
 *     • an instance is already in flight → return it, enqueue nothing
 *     • otherwise                        → create it pending, enqueue, return it
 *
 * A variant is always generated **from the primitive's locked base reference**, so an axis
 * modulates world, lighting, and pose while identity stays locked. Everything resolved is
 * cached and reused across stories and mediums: the winter-night Moon Garden is generated
 * once, then reused forever.
 *
 * Generation itself is never in here. `resolve` hands a `pending` record to `enqueue` and
 * returns; **no image model is ever called from the engine**.
 */
import { canonicalVariant, sceneRenderId, variantInstanceId } from './address.js';
import { assertCharacterApproved, assertVariantAllowed, isNeutralVariant } from './approval.js';
import { parsePrimitivePath, primitivePath } from './collections.js';
import { AlreadyExistsError, InvalidRequestError } from './errors.js';
import { sceneLayerPlan } from './scene.js';
import { systemNow } from './store.js';
/** The fields a fresh request clears, so a retried address never carries a stale attempt. */
const UNSTARTED = {
    status: 'pending',
    assetPath: undefined,
    attempts: undefined,
    claimedAt: undefined,
    claimToken: undefined,
    error: undefined,
    approved: undefined,
    approvedAt: undefined,
    approvedBy: undefined,
};
/**
 * Resolve one `(primitive, version, variant, theme)` point to its cached instance,
 * creating and enqueuing a `pending` one on a miss.
 *
 * A non-base variant is gated first (`assertVariantAllowed`), before the store is touched
 * for writing — a refused request incurs no cost and leaves no junk record.
 */
export async function resolve(deps, request) {
    const now = deps.now ?? systemNow;
    const variant = canonicalVariant(request.variant);
    const isBase = isNeutralVariant(variant);
    if (request.isBaseReference !== undefined && request.isBaseReference !== isBase) {
        throw new InvalidRequestError(`isBaseReference is derived from the variant, not declared: variant ${JSON.stringify(variant)} is ${isBase ? '' : 'not '}the neutral variant, but the request says isBaseReference=${String(request.isBaseReference)}`);
    }
    if (request.primitivePath !== undefined && parsePrimitivePath(request.primitivePath) === null) {
        throw new InvalidRequestError(`primitivePath is not a legal primitive path: ${JSON.stringify(request.primitivePath)}`);
    }
    if (!isBase) {
        const primitive = await loadPrimitiveFor(deps, request);
        const baseId = variantInstanceId(request.primitiveId, request.primitiveVersion, {}, request.themeId);
        const baseInstance = await deps.store.get(baseId);
        assertVariantAllowed(primitive, baseInstance, {
            primitiveVersion: request.primitiveVersion,
            variant,
        });
    }
    const id = variantInstanceId(request.primitiveId, request.primitiveVersion, variant, request.themeId);
    const existing = await deps.store.get(id);
    if (existing !== null) {
        // A ready hit is the whole point of content addressing: free, and shared by everyone.
        if (existing.status === 'ready' && request.force !== true)
            return existing;
        // Already in flight — someone else is paying for this exact address. Do not re-enqueue.
        if ((existing.status === 'pending' || existing.status === 'generating') &&
            request.force !== true) {
            return existing;
        }
        // An error, or a cost-confirmed force. Same address — reset it, never a new id.
        await deps.store.update(id, { ...UNSTARTED });
        const reset = { ...existing, ...UNSTARTED };
        await deps.enqueue?.(reset);
        return reset;
    }
    const record = {
        id,
        primitiveId: request.primitiveId,
        primitiveVersion: request.primitiveVersion,
        themeId: request.themeId,
        variant,
        status: 'pending',
        createdAt: now(),
        ...(isBase ? { isBaseReference: true } : {}),
        ...(request.primitivePath !== undefined ? { primitivePath: request.primitivePath } : {}),
        ...(request.requestedBy !== undefined ? { requestedBy: request.requestedBy } : {}),
    };
    try {
        await deps.store.create(record);
    }
    catch (error) {
        if (error instanceof AlreadyExistsError) {
            // Lost the race. Someone else created this address; use theirs and enqueue nothing.
            const theirs = await deps.store.get(id);
            if (theirs !== null)
                return theirs;
        }
        throw error;
    }
    await deps.enqueue?.(record);
    return record;
}
async function loadPrimitiveFor(deps, request) {
    const primitive = request.primitive ??
        (await deps.loadPrimitive?.(request.primitiveId, request.primitivePath)) ??
        null;
    if (primitive === null) {
        throw new InvalidRequestError(`resolving a variant of ${request.primitiveId} needs the primitive: pass request.primitive or deps.loadPrimitive`);
    }
    if (request.kind !== undefined && request.kind !== primitive.kind) {
        throw new InvalidRequestError(`request.kind is ${request.kind} but primitive ${primitive.id} is a ${primitive.kind}`);
    }
    return primitive;
}
/**
 * The provenance path for one ref: what the caller said, or what the primitive implies.
 *
 * Derived rather than required, because a caller that already handed over the primitive has
 * told us everything the path encodes — the scope and the owner.
 */
function scenePrimitivePath(options, id) {
    const given = options.primitivePaths?.[id];
    if (given !== undefined)
        return given;
    const primitive = options.primitives?.[id];
    if (primitive === undefined)
        return undefined;
    return primitivePath(primitive.scope, primitive.ownerId, primitive.id);
}
/**
 * Resolve every piece a scene needs, then address the scene itself.
 *
 * The cast is gated first: every character must be locked before anything is written, so an
 * unapproved fox refuses the whole scene rather than half-creating it. Then each ref in the
 * layer plan resolves (free where cached), and the scene render is created `pending` with
 * `waitingOn` naming the instances that are not ready yet — the orchestration state the
 * `onVariantInstanceReady` trigger reads to wake this scene later.
 *
 * Compositing is not done here (`engine/04`), so `layers` stays empty and the status stays
 * `pending` even when every instance is already ready.
 */
export async function resolveSceneRender(deps, scene, themeId, options = {}) {
    const now = deps.now ?? systemNow;
    // 1. Gate the cast. Before any write.
    for (const character of scene.characters) {
        const primitive = await scenePrimitive(deps, options, character.primitiveId);
        const baseId = variantInstanceId(character.primitiveId, character.version, {}, themeId);
        assertCharacterApproved(primitive, await deps.store.get(baseId));
    }
    // 2. Resolve every piece, in the order the compositor will stack them.
    const plan = sceneLayerPlan(scene);
    const usedInstances = [];
    const waitingOn = [];
    for (const entry of plan) {
        const path = scenePrimitivePath(options, entry.ref.primitiveId);
        const instance = await resolve(deps, {
            primitiveId: entry.ref.primitiveId,
            primitiveVersion: entry.ref.version,
            variant: entry.ref.variant,
            themeId,
            primitive: options.primitives?.[entry.ref.primitiveId],
            ...(options.requestedBy !== undefined ? { requestedBy: options.requestedBy } : {}),
            ...(path !== undefined ? { primitivePath: path } : {}),
        });
        usedInstances.push(instance.id);
        if (instance.status !== 'ready')
            waitingOn.push(instance.id);
    }
    // 3. Address the scene. Identical composition → identical id → no second create.
    const id = sceneRenderId(scene, themeId);
    const existing = await deps.sceneStore.get(id);
    if (existing !== null)
        return existing;
    const record = {
        id,
        themeId,
        status: 'pending',
        layers: [],
        usedInstances,
        waitingOn,
        scene,
        createdAt: now(),
        ...(options.requestedBy !== undefined ? { requestedBy: options.requestedBy } : {}),
    };
    try {
        await deps.sceneStore.create(record);
    }
    catch (error) {
        if (error instanceof AlreadyExistsError) {
            const theirs = await deps.sceneStore.get(id);
            if (theirs !== null)
                return theirs;
        }
        throw error;
    }
    return record;
}
async function scenePrimitive(deps, options, primitiveId) {
    const primitive = options.primitives?.[primitiveId] ?? (await deps.loadPrimitive?.(primitiveId)) ?? null;
    if (primitive === null) {
        throw new InvalidRequestError(`rendering a scene needs primitive ${primitiveId}: pass options.primitives or deps.loadPrimitive`);
    }
    return primitive;
}
//# sourceMappingURL=resolve.js.map